import { IndexedFilters } from '@/catalog/types/filters';
import { DrawerDialog } from '@/common/components/drawer-dialog';
import FilterAccordion from './filter-accordion/filter-accordion';
import { Button } from '@/shadcn-ui/button';
import FilterPills from './filter-pills/filter-pills';
import { useBreakpoint } from '@/common/hooks/use-breakpoint';
import { InventoryFiltersResponse } from '@/catalog/hooks/use-inventory-filters';
import { useEffect } from 'react';
import { FilterCounts } from '@/catalog/types/catalog-market-loader';

interface InventoryFilterDrawerDialogProps extends InventoryFiltersResponse {
  filterOptions: IndexedFilters;
  open: boolean;
  handleClose: () => void;
  vehicleTotal?: number;
  filterCounts?: FilterCounts;
  selectedPlanMileage?: number;
  canViewSwap?: boolean;
  vehicleWaitlistEnabled?: boolean;
}

/**
 * Drawer dialog to show filters for inventory page on mobile views. For this specific UX, the drawer dialog is available for all screens
 * smaller than LG size screens.
 */
export default function InventoryFilterDrawerDialog({
  filterOptions,
  selectedFilters,
  setSelectedFilters,
  handleClearAll,
  hasNoFilters,
  filterCount,
  filterCounts,
  selectedPlanMileage,
  open,
  handleClose,
  vehicleTotal,
  canViewSwap,
  vehicleWaitlistEnabled = false,
}: InventoryFilterDrawerDialogProps) {
  const breakpoint = useBreakpoint();

  // Force close the dialog on desktop
  useEffect(() => {
    if (breakpoint === 'desktop') {
      handleClose();
    }
  }, [breakpoint, handleClose]);

  return (
    <DrawerDialog
      open={open}
      handleClose={handleClose}
      title="Filters"
      variant="center-title"
      childrenContainerClassName="p-0 overflow-hidden flex"
    >
      {/* Need to recreate the scrolling functionality inside dialog content so that the drawer dialog can hide
      the overflowing corners of the accordion content when mid-scroll */}
      <div className="scrollbar-hide relative flex-[1_1_0%] overflow-y-scroll pb-[80px]">
        {!hasNoFilters ? (
          // Need z-1 here to bring this block above the icon on the accordion item when the accordion item is in an open state
          <div className="sticky top-0 z-1 flex w-full flex-col gap-[16px] bg-flexcar-neutral90 p-[24px]">
            <div className="flex w-full items-center justify-between">
              <p className="body2-strong">Applied filters ({filterCount})</p>
              <Button variant="clear" onClick={handleClearAll} className="h-auto">
                <p className="body3-strong text-flexcar-blue40">Clear all</p>
              </Button>
            </div>
            <FilterPills
              selectedFilters={selectedFilters}
              setSelectedFilters={setSelectedFilters}
            />
          </div>
        ) : null}

        <FilterAccordion
          filterOptions={filterOptions}
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
          handleClose={handleClose}
          filterCounts={filterCounts}
          selectedPlanMileage={selectedPlanMileage}
          canViewSwap={canViewSwap}
          vehicleWaitlistEnabled={vehicleWaitlistEnabled}
        />
      </div>
      {/* Sticky footer */}
      <div className="absolute bottom-0 z-1 flex w-full rounded-b-[16px] bg-flexcar-neutral100 p-[16px] shadow-modalFooterShadow">
        <Button
          variant="primary"
          onClick={handleClose}
          className="h-fit w-full px-[16px] py-[12px]"
        >
          <p className="body-strong text-center text-flexcar-neutral100">
            View {vehicleTotal} results
          </p>
        </Button>
      </div>
    </DrawerDialog>
  );
}
